import { Grid, Popper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library';
import {
  SvgCalendar,
  DottedText,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import Info from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import CustomDateMonthGrid from '../CustomDateMonthGrid';
import CustomDateMonthSelector from '../CustomDateMonthSelector';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { useStores } from '../../../stores';
import { MonthMap } from '../../../core-utils/helper-utils/EditDuration';
import {
  DATE_FORMAT,
  MONTH_YEAR_FORMAT,
} from '../../../core-utils/messages/CommonConstants';
import { getPlanPeriodDates } from '../../../core-utils/helper-utils/reportsWorkFlowHelper';
import { toJS } from 'mobx';

export interface EditDurationModalProps {
  isYearGrid: boolean;
  handleDateChange: any;
  isCustomQueryPage?: boolean;
  isSummaryScreen?: boolean;
  showOnlyMainComponent?: boolean;
  isQuaterlyReport?: boolean;
}
const EditDurationModal: React.FC<EditDurationModalProps> = ({
  isYearGrid,
  handleDateChange,
  isCustomQueryPage,
  isSummaryScreen,
  showOnlyMainComponent,
  isQuaterlyReport,
}) => {
  const [startMonthYearString, setStartMonthYearString] = useState<string>('');
  const [endMonthYearString, setEndMonthYearString] = useState<string>('');
  const employerGroupStore = useStores().employerGroupStore;
  const memberStore = useStores().membersStore;
  const customQueriesStore = useStores().customQueriesStore;
  const [minPossibleDate, setMinPossibleDate] = useState<moment.Moment>();
  const [maxPossibleDate, setMaxPossibleDate] = useState<moment.Moment>();
  const [momentStartDate, setMomentStartDate] = useState<moment.Moment>();
  const [momentEndDate, setMomentEndDate] = useState<moment.Moment>();
  const [startMonthArray, setStartMonthArray] = useState<string[]>([]);
  const [endMonthArray, setEndMonthArray] = useState<string[]>([]);
  const [planStartMonth, setPlanStartMonth] = useState<string>('');
  const [startYearArray, setStartYearArray] = useState<string[]>([]);
  const [endYearArray, setEndYearArray] = useState<string[]>([]);
  const [startYearIndex, setStartYearIndex] = useState<number>(0);
  const [endYearIndex, setEndYearIndex] = useState<number>(0);

  // states
  const [
    gridAnchorElement,
    setGridAnchorElement,
  ] = useState<null | HTMLElement>(null);
  const [
    startDateAnchorElement,
    setStartDateAnchorElement,
  ] = useState<null | HTMLElement>(null);
  const [
    endDateAnchorElement,
    setEndDateAnchorElement,
  ] = useState<null | HTMLElement>(null);

  // styles
  const useStyles = makeStyles()({
    root: {
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      width: '160px',
      height: '27px',
      background: Boolean(gridAnchorElement)
        ? patientAccessTheme.palette.grey[50]
        : patientAccessTheme.palette.status.white,
      border: Boolean(gridAnchorElement)
        ? `1px solid ${patientAccessTheme.palette.primary[200]}`
        : `1px solid ${patientAccessTheme.palette.grey[300]}`,
      borderRadius: '4px',
      color: Boolean(gridAnchorElement)
        ? patientAccessTheme.palette.primary.main
        : patientAccessTheme.palette.text.highemphasis,
      padding: '0px',
      cursor: 'pointer',
    },
    paper: {
      position: 'absolute',
      top: '27px !important',
      right: isQuaterlyReport ? '0px !important' : '',
      left: isQuaterlyReport ? '' : '0px !important',
      zIndex: 900,
      '& .MuiPopover-paper': {
        boxShadow: 'none',
      },
    },
    bottomContainerItem: {
      marginRight: '12px',
    },
    selectDatesInfo: {
      boxSizing: 'border-box',
      width: '692px',
      height: '37px',
      background: '#F2F5FF',
      border: '1px solid #E5E9F0',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      marginLeft: '24px',
      marginBottom: '20px',
    },
    selectDatesField: {
      boxSizing: 'border-box',
      background: '#FFFFFF',
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      borderRadius: '4px',
      height: '39px',
      width: '193px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginRight: '12px',
      cursor: 'pointer',
    },
    startDatePopper: {
      top: '38px !important',
      left: '70px !important',
      zIndex: 900,
      '& .MuiPopover-paper': {
        boxShadow: 'none',
      },
    },
    endDatePopper: {
      top: '38px !important',
      left: '339px !important',
      zIndex: 900,
      '& .MuiPopover-paper': {
        boxShadow: 'none',
      },
    },
  });
  const { classes } = useStyles();

  const getDates = async () => {
    const startDate =
      employerGroupStore?.selectedDuration?.startDate !== ''
        ? moment(employerGroupStore?.selectedDuration?.startDate, 'YYYY-MM-DD')
        : null;
    const endDate =
      employerGroupStore?.selectedDuration?.endDate !== ''
        ? moment(employerGroupStore?.selectedDuration?.endDate)
        : null;

    const dateFilterResponse = isCustomQueryPage
      ? getPlanPeriodDates(toJS(customQueriesStore.employerGroups))
      : employerGroupStore.employerGroupResponseData;

    const {
      start_month,
      start_year,
      end_month,
      end_year,
      plan_start_month,
      plan_end_month,
      recent_plan_year,
    } = dateFilterResponse;
    if (isSummaryScreen) {
      const monthNumber = MonthMap.get(dateFilterResponse.end_month)
        ?.monthNumber;
      const defaultDate = new Date(
        monthNumber + '-01-' + dateFilterResponse.end_year,
      );
      memberStore.addStartDate(
        moment(defaultDate).subtract(11, 'months').format(DATE_FORMAT),
      );
      memberStore.addEndDate(
        moment(defaultDate).endOf('month').format(DATE_FORMAT),
      );
    }

    let minDate = moment(
      MonthMap.get(start_month)?.monthNumber + '-01-' + start_year,
      'MM-DD-YYYY',
    );
    let maxDate = moment(
      MonthMap.get(isCustomQueryPage ? end_month : plan_end_month)
        ?.monthNumber +
        '-01-' +
        (isCustomQueryPage ? end_year : recent_plan_year),
      'MM-DD-YYYY',
    ).endOf('month');

    setMaxPossibleDate(maxDate);

    const defaultStartDate = moment(maxDate, 'MM-DD-YYYY')
      .clone()
      .subtract(11, 'months')
      .startOf('month');

    const localStartDate = startDate ?? moment.max(minDate, defaultStartDate);

    const localEndDate =
      endDate ??
      (maxDate?.isAfter(
        localStartDate?.clone().add(11, 'months').endOf('month'),
      )
        ? localStartDate?.clone().add(11, 'months').endOf('month')
        : maxDate);

    setMomentStartDate(localStartDate);
    setMomentEndDate(localEndDate);

    const newStartArr: any[] = [];
    const newEndArr: any[] = [];
    const startComp = moment(
      MonthMap.get(start_month)?.monthNumber + '-01-' + start_year,
      'MM-DD-YYYY',
    ).endOf('month');
    let startyear;
    while (startComp < maxDate) {
      startyear =
        plan_end_month === 'Dec' ? maxDate.year() : maxDate.year() - 1;
      newStartArr.push(startyear.toString());
      newEndArr.push(
        (plan_end_month === 'Dec' ? startyear : startyear + 1).toString(),
      );
      maxDate = maxDate?.clone().subtract(1, 'year');
    }
    minDate = moment(
      MonthMap.get(start_month)?.monthNumber + '-01-' + startyear,
      'MM-DD-YYYY',
    );
    setMinPossibleDate(minDate);

    newStartArr.sort((a, b) => parseInt(a) - parseInt(b));
    newEndArr.sort((a, b) => parseInt(a) - parseInt(b));

    employerGroupStore.setSelectedDuration(
      localStartDate.format('YYYY-MM-DD'),
      localEndDate.format('YYYY-MM-DD'),
    );
    customQueriesStore.setCurrentDates(
      localStartDate.format('YYYY-MM-DD'),
      localEndDate.format('YYYY-MM-DD'),
    );

    setStartMonthYearString(localStartDate.format(MONTH_YEAR_FORMAT));
    setEndMonthYearString(localEndDate.format(MONTH_YEAR_FORMAT));
    setPlanStartMonth(plan_start_month);
    setStartYearArray([...newStartArr]);
    setStartYearIndex(
      newStartArr.indexOf(localStartDate?.year().toString() ?? ''),
    );
    setEndYearArray([...newEndArr]);
    setEndYearIndex(newEndArr.indexOf(localEndDate?.year().toString() ?? ''));
  };

  const getMonthArray = (
    year: number,
    type: 'start' | 'end',
    currentDate?: moment.Moment,
  ) => {
    const monthArray: string[] = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const maxYear = maxPossibleDate?.year() ?? 0;
    const minYear = minPossibleDate?.year() ?? 5000;
    const currentStartDate = currentDate ?? momentStartDate;

    if (type === 'start') {
      if (year > minYear && year < maxYear) {
        return monthArray;
      } else if (year === minYear) {
        return monthArray.slice(minPossibleDate?.month() ?? 0);
      } else if (year === maxYear) {
        return monthArray.slice(0, (maxPossibleDate?.month() ?? 0) + 1);
      } else {
        return [];
      }
    } else {
      if (
        year === currentStartDate?.year() &&
        year === maxPossibleDate?.year()
      ) {
        return monthArray.slice(
          currentStartDate?.month(),
          maxPossibleDate?.month() + 1,
        );
      } else if (
        year === currentStartDate?.year() &&
        year !== maxPossibleDate?.year()
      ) {
        return monthArray.slice(currentStartDate?.month() ?? 0);
      } else if (
        year !== currentStartDate?.year() &&
        year !== maxPossibleDate?.year()
      ) {
        return monthArray.slice(0, currentStartDate?.month() ?? 0);
      } else if (
        year !== currentStartDate?.year() &&
        year === maxPossibleDate?.year()
      ) {
        const minimumMonth = Math.min(
          maxPossibleDate?.month() ?? 0,
          currentStartDate?.month() ?? 0,
        );

        return monthArray.slice(0, minimumMonth);
      } else {
        return [];
      }
    }
  };

  const handleYearChange = (year: string) => {
    const currentDate = new Date(
      MonthMap.get(planStartMonth)?.monthNumber + '-01-' + year,
    );
    setStartMonthYearString(moment(currentDate).format(MONTH_YEAR_FORMAT));
    setMomentStartDate(moment(currentDate));
    setMomentEndDate(moment(currentDate).clone().add(11, 'months'));
    setEndMonthYearString(
      moment(currentDate).clone().add(11, 'months').format(MONTH_YEAR_FORMAT),
    );

    setStartDateAnchorElement(null);
  };

  const handleStartChange = (month: string) => {
    const localStartDate = moment(
      MonthMap.get(month)?.monthNumber +
        '-01-' +
        startYearArray[startYearIndex],
      'MM-DD-YYYY',
    );
    setPlanStartMonth(month);
    setMomentStartDate(localStartDate);

    const localEndDate = maxPossibleDate?.isAfter(
      moment(localStartDate)?.clone().add(11, 'months').endOf('month'),
    )
      ? moment(localStartDate)?.clone().add(11, 'months').endOf('month')
      : maxPossibleDate;

    setMomentEndDate(localEndDate);

    setStartMonthYearString(localStartDate?.format(MONTH_YEAR_FORMAT) ?? '');
    setEndMonthYearString(localEndDate?.format(MONTH_YEAR_FORMAT) ?? '');

    setEndMonthArray(
      getMonthArray(localEndDate?.year() ?? -1, 'end', localStartDate),
    );

    const endYearArray = [];
    endYearArray.push(localStartDate?.year().toString() ?? '');
    if ((localStartDate?.month() ?? 0) > 0) {
      endYearArray.push(((localStartDate?.year() ?? 0) + 1).toString());
    }
    setEndYearArray([...endYearArray]);
    setEndYearIndex(
      endYearArray.indexOf(localEndDate?.year().toString() ?? ''),
    );
  };

  const handleStartYearChange = (isIncrement: boolean, arrayLength: number) => {
    let monthArray: any = [];
    if (setStartYearIndex) {
      if (isIncrement) {
        setStartYearIndex((prev) => {
          if (prev < arrayLength - 1) {
            monthArray = getMonthArray(
              Number(startYearArray[prev + 1]),
              'start',
            );
            setStartMonthArray([...monthArray]);
            return prev + 1;
          }
          return prev;
        });
      } else {
        setStartYearIndex((prev) => {
          if (prev > 0) {
            monthArray = getMonthArray(
              Number(startYearArray[prev - 1]),
              'start',
            );
            setStartMonthArray([...monthArray]);
            return prev - 1;
          }
          return prev;
        });
      }
    }
  };
  const handleEndChange = (month: string) => {
    const localEndDate = moment(
      MonthMap.get(month)?.monthNumber + '-01-' + endYearArray[endYearIndex],
      'MM-DD-YYYY',
    );
    setMomentEndDate(localEndDate?.clone().endOf('month'));
    setEndMonthYearString(localEndDate?.format(MONTH_YEAR_FORMAT) ?? '');
  };
  const handleEndYearChange = (isIncrement: boolean, arrayLength: number) => {
    let monthArray: any = [];
    if (setEndYearIndex) {
      if (isIncrement) {
        setEndYearIndex((prev) => {
          if (prev < arrayLength - 1) {
            monthArray = getMonthArray(Number(endYearArray[prev + 1]), 'end');
            setEndMonthArray([...monthArray]);
            return prev + 1;
          }
          return prev;
        });
      } else {
        setEndYearIndex((prev) => {
          if (prev > 0) {
            monthArray = getMonthArray(Number(endYearArray[prev - 1]), 'end');
            setEndMonthArray([...monthArray]);
            return prev - 1;
          }
          return prev;
        });
      }
    }
  };

  const handleSubmit = () => {
    setGridAnchorElement(null);
    setStartDateAnchorElement(null);
    setEndDateAnchorElement(null);

    if (momentStartDate && momentEndDate) {
      employerGroupStore.setSelectedDuration(
        momentStartDate.format('YYYY-MM-DD'),
        momentEndDate.format('YYYY-MM-DD'),
      );
      customQueriesStore.setCurrentDates(
        momentStartDate.format('YYYY-MM-DD'),
        momentEndDate.format('YYYY-MM-DD'),
      );
      handleDateChange();
    }
  };

  const id = Boolean(gridAnchorElement) ? 'simple-popper' : undefined;
  useEffect(() => {
    getDates();
  }, []);

  const getMainComponent = () => {
    return (
      <Grid
        container
        style={{
          boxSizing: 'border-box',
          width: '740px',
          height: '170px',
          background: '#FFFFFF',
          border: '1px solid #E5E9F0',
          boxShadow: '0px 2px 24px 4px rgba(55, 70, 160, 0.08)',
          borderRadius: '4px',
        }}
        sx={{
          alignContent: 'flex-start',
        }}
      >
        {/* select start and end date */}
        <Grid
          container
          style={{
            margin: '18px 24px 12px 24px',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            fontSize="12px"
            lineHeight="22px"
            fontType="medium"
            textColor={patientAccessTheme.palette.text.mediumemphasis}
          >
            Select a start and end date
          </Typography>
        </Grid>
        {/* info */}
        <Grid className={classes.selectDatesInfo}>
          <Info
            height={'16px'}
            width="16px"
            style={{ marginRight: '8px', marginLeft: '10px' }}
          />
          <Typography
            fontSize="12px"
            lineHeight="177%"
            textColor={patientAccessTheme.palette.text.mediumemphasis}
          >
            {isYearGrid
              ? 'Data is computed annually, please choose dates that are exactly one year apart for this screen'
              : 'Data is computed monthly, please choose dates that are exactly Zero months - One year apart for this screen'}
          </Typography>
        </Grid>
        <Grid
          container
          style={{ marginLeft: '24px', position: 'relative' }}
          sx={{
            alignItems: 'center',
          }}
        >
          <Grid className={classes.bottomContainerItem}>
            <Typography
              fontSize="12px"
              lineHeight="22px"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.mediumemphasis}
            >
              Start Date
            </Typography>
          </Grid>
          <CustomDateMonthSelector
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              setStartDateAnchorElement(
                startDateAnchorElement ? null : event.currentTarget,
              );
            }}
            monthAndYear={startMonthYearString}
          />
          <Popper
            open={Boolean(startDateAnchorElement)}
            anchorEl={startDateAnchorElement}
            placement="bottom-start"
            className={classes.startDatePopper}
            disablePortal={true}
            modifiers={[
              {
                name: 'preventOverflow',
                enabled: true,
              },
            ]}
          >
            <CustomDateMonthGrid
              currentMonth={planStartMonth}
              currentYear={
                isYearGrid
                  ? startMonthYearString?.slice(-4)
                  : startYearArray[startYearIndex]
              }
              yearArray={startYearArray}
              monthArray={startMonthArray}
              isYearGrid={isYearGrid}
              handleChange={isYearGrid ? handleYearChange : handleStartChange}
              handleYearChange={handleStartYearChange}
              currentDate={momentStartDate ?? moment()}
              yearIndex={startYearIndex}
              setAnchorElement={setStartDateAnchorElement}
            />
          </Popper>
          <Grid className={classes.bottomContainerItem}>
            <Typography
              fontSize="12px"
              lineHeight="22px"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.mediumemphasis}
            >
              End Date
            </Typography>
          </Grid>
          <CustomDateMonthSelector
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              setEndDateAnchorElement(
                endDateAnchorElement ? null : event.currentTarget,
              );
            }}
            monthAndYear={endMonthYearString}
            isDisabled={isYearGrid}
          />
          <Popper
            open={Boolean(endDateAnchorElement)}
            anchorEl={endDateAnchorElement}
            placement="bottom-start"
            className={classes.endDatePopper}
            disablePortal={true}
            modifiers={[
              {
                name: 'preventOverflow',
                enabled: true,
              },
            ]}
          >
            <CustomDateMonthGrid
              currentMonth={endMonthYearString?.slice(0, 3)}
              currentYear={
                isYearGrid
                  ? endMonthYearString?.slice(-4)
                  : endYearArray[endYearIndex]
              }
              yearArray={isYearGrid ? startYearArray : endYearArray}
              monthArray={endMonthArray}
              isYearGrid={isYearGrid}
              handleChange={handleEndChange}
              handleYearChange={handleEndYearChange}
              currentDate={momentEndDate ?? moment()}
              yearIndex={endYearIndex}
              setAnchorElement={setEndDateAnchorElement}
            />
          </Popper>
          <Grid style={{ cursor: 'pointer' }} onClick={handleSubmit}>
            <DottedText text={'Submit Dates'} />
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid style={{ position: 'relative' }}>
      {showOnlyMainComponent ? (
        getMainComponent()
      ) : (
        <>
          {/* Main button */}
          <Grid
            container
            className={classes.root}
            component="button"
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              setStartMonthArray(
                getMonthArray(momentStartDate?.year() ?? -1, 'start'),
              );
              setEndMonthArray(
                getMonthArray(
                  momentEndDate?.year() ?? -1,
                  'end',
                  momentStartDate,
                ),
              );
              setGridAnchorElement(
                gridAnchorElement ? null : event.currentTarget,
              );
              if (gridAnchorElement) {
                setStartDateAnchorElement(null);
                setEndDateAnchorElement(null);
              }
            }}
            aria-describedby={id}
          >
            <SvgCalendar
              height="16px"
              width="16px"
              color={
                Boolean(gridAnchorElement)
                  ? patientAccessTheme.palette.primary.main
                  : '#7C98B6'
              }
              style={{
                marginRight: '8px',
              }}
            />

            <Grid>
              <Typography
                fontSize="12px"
                lineHeight="20px"
                fontType="medium"
                margin="0px 4px 0px 0px"
              >
                {startMonthYearString?.slice(0, 3) +
                  ' ' +
                  startMonthYearString?.slice(-4) +
                  ' -'}
              </Typography>
            </Grid>
            <Grid>
              <Typography fontSize="12px" lineHeight="20px" fontType="medium">
                {endMonthYearString?.slice(0, 3) +
                  ' ' +
                  endMonthYearString?.slice(-4)}
              </Typography>
            </Grid>
          </Grid>
          {/* first Popper */}
          <Popper
            id={id}
            open={Boolean(gridAnchorElement)}
            anchorEl={gridAnchorElement}
            className={classes.paper}
            disablePortal={true}
            modifiers={[
              {
                name: 'preventOverflow',
                enabled: true,
              },
            ]}
            style={{
              right: isQuaterlyReport ? '0px !important' : '',
              left: isQuaterlyReport ? '' : '0px !important',
            }}
          >
            {getMainComponent()}
          </Popper>
        </>
      )}
    </Grid>
  );
};
export default React.memo(EditDurationModal);
